import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import {
  INVENTORY_GIFTS_PARAMS,
  MARKETPLACE_GIFTS_PARAMS,
  usePlatformGifts,
  usePurchasePlatformGift,
  useUserGifts,
} from "@/entities/gift"
import {
  GiftDetailsSheet,
  GiftsGrid,
  InventoryEmptyActions,
  InventoryEmptyState,
  InventoryTabs,
  MarketplaceGrid,
  getGiftRelayBotTelegramUrl,
  type InventoryTabKey,
} from "@/features/inventory"
import { platformGiftToUserGift } from "@/features/inventory/lib/platformGift"
import { DepositSheet } from "@/features/wallet"
import type {
  PlatformGift,
  PlatformGiftPurchaseErrorCode,
  UserGift,
} from "@/shared/api/types"
import { translatePath, useI18n } from "@/shared/i18n"
import { ApiError } from "@/shared/api/client"
import { telegram } from "@/shared/lib/telegram"
import { makeErrorToast } from "@/shared/lib/toast-error"
import { makeSuccessToast } from "@/shared/lib/toast-success"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

function readPurchaseErrorCode(err: unknown): PlatformGiftPurchaseErrorCode | null {
  if (!(err instanceof ApiError)) return null
  const d = err.details
  if (d && typeof d === "object" && "code" in d) {
    const code = (d as { code?: unknown }).code
    return typeof code === "string"
      ? (code as PlatformGiftPurchaseErrorCode)
      : null
  }
  return null
}

function purchaseErrorMessage(code: PlatformGiftPurchaseErrorCode | null): string {
  switch (code) {
    case "platform_gift_price_changed":
      return translatePath("marketplace.errorPriceChanged")
    case "insufficient_balance":
      return translatePath("marketplace.errorInsufficientBalance")
    case "gift_not_available_on_platform":
    case "gift_not_for_sale":
    case "gift_not_found":
      return translatePath("marketplace.errorGiftGone")
    case "gift_floor_price_unavailable":
      return translatePath("marketplace.errorPriceUnavailable")
    default:
      return translatePath("marketplace.errorGeneric")
  }
}

const Inventory = () => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Deep link: /inventory?tab=marketplace lands on the marketplace tab.
  const initialTab: InventoryTabKey =
    searchParams.get("tab") === "marketplace" ? "marketplace" : "assets"
  const [activeTab, setActiveTab] = useState<InventoryTabKey>(initialTab)

  const handleTabChange = (key: InventoryTabKey) => {
    setActiveTab(key)
    // Keep the URL in sync so back/forward + share work.
    const next = new URLSearchParams(searchParams)
    if (key === "marketplace") next.set("tab", "marketplace")
    else next.delete("tab")
    setSearchParams(next, { replace: true })
  }

  const [selectedGift, setSelectedGift] = useState<UserGift | null>(null)
  const [selectedMarketGift, setSelectedMarketGift] =
    useState<PlatformGift | null>(null)
  const [isDepositOpen, setIsDepositOpen] = useState(false)
  const [depositInitialAmount, setDepositInitialAmount] = useState<number | null>(
    null
  )

  // ── Assets tab ──────────────────────────────────────────────────────────
  const {
    data: assetsData,
    isLoading: isAssetsLoading,
    isError: isAssetsError,
    refetch: refetchAssets,
    isFetching: isAssetsFetching,
  } = useUserGifts(INVENTORY_GIFTS_PARAMS, { enabled: activeTab === "assets" })

  const gifts = assetsData?.items ?? []
  const isAssetsLoadingFirst =
    (isAssetsLoading || isAssetsFetching) && gifts.length === 0
  const showAssetsEmpty =
    activeTab === "assets" &&
    !isAssetsLoadingFirst &&
    !isAssetsError &&
    gifts.length === 0

  // ── Marketplace tab ─────────────────────────────────────────────────────
  const {
    data: marketData,
    isLoading: isMarketLoading,
    isError: isMarketError,
    refetch: refetchMarket,
  } = usePlatformGifts(MARKETPLACE_GIFTS_PARAMS, {
    enabled: activeTab === "marketplace",
  })
  const marketGifts = marketData?.items ?? []

  const purchaseMutation = usePurchasePlatformGift()
  const buyingGiftId =
    purchaseMutation.isPending && purchaseMutation.variables
      ? purchaseMutation.variables.externalGiftId
      : null

  // Buy on a card opens the confirmation sheet (gift details + Buy CTA).
  const handleBuy = (gift: PlatformGift) => {
    setSelectedMarketGift(gift)
  }

  // Buy CTA inside the sheet — fires the actual purchase.
  const confirmBuy = () => {
    if (!selectedMarketGift) return
    purchaseMutation.mutate(
      {
        externalGiftId: selectedMarketGift.external_gift_id,
        expectedPriceNanoTon: selectedMarketGift.purchase_price_nano_ton,
      },
      {
        onSuccess: (_data, variables) => {
          trackAnalyticsEvent(AnalyticsEvent.MarketplaceGiftPurchased, {
            external_gift_id: variables.externalGiftId,
            price_ton: variables.expectedPriceNanoTon / 1e9,
          })
          makeSuccessToast({ label: t("marketplace.purchased") })
          setSelectedMarketGift(null)
        },
        onError: (err) => {
          makeErrorToast({ label: purchaseErrorMessage(readPurchaseErrorCode(err)) })
        },
      }
    )
  }

  // ── Common handlers ─────────────────────────────────────────────────────
  const openRelayBotChat = () => {
    telegram.openTelegramLink(getGiftRelayBotTelegramUrl())
  }

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) setSelectedGift(null)
  }

  const handleRequestDeposit = (shortfallTon: number) => {
    setDepositInitialAmount(shortfallTon > 0 ? shortfallTon : null)
    setIsDepositOpen(true)
  }

  // "Play with this gift": just switch to the PvP screen (no bet placed here).
  const handlePlayWithGift = () => {
    setSelectedGift(null)
    navigate("/")
  }

  const goToMarketplace = () => handleTabChange("marketplace")

  return (
    <div className="flex flex-col">
      <div className="px-4 py-4">
        <h1 className="text-[24px] font-semibold leading-[29px] tracking-[-0.5px] text-white">
          {t("inventory.title")}
        </h1>
      </div>

      <InventoryTabs active={activeTab} onChange={handleTabChange} />

      {activeTab === "assets" &&
        (showAssetsEmpty ? (
          <InventoryEmptyState onHandleClick={openRelayBotChat} />
        ) : (
          <GiftsGrid
            gifts={gifts}
            isLoading={isAssetsLoadingFirst}
            isError={isAssetsError}
            onRetry={() => refetchAssets()}
            onSelect={setSelectedGift}
          />
        ))}

      {showAssetsEmpty && (
        <InventoryEmptyActions
          onSend={openRelayBotChat}
          onGoShopping={goToMarketplace}
          shopDisabled={false}
        />
      )}

      {activeTab === "marketplace" && (
        <MarketplaceGrid
          gifts={marketGifts}
          isLoading={isMarketLoading}
          isError={isMarketError}
          buyingGiftId={buyingGiftId}
          onBuy={handleBuy}
          onRetry={() => refetchMarket()}
        />
      )}

      <GiftDetailsSheet
        open={selectedGift !== null}
        onOpenChange={handleSheetOpenChange}
        gift={selectedGift}
        onRequestDeposit={handleRequestDeposit}
        onPlay={handlePlayWithGift}
      />

      <GiftDetailsSheet
        open={selectedMarketGift !== null}
        onOpenChange={(o) => {
          if (!o) setSelectedMarketGift(null)
        }}
        gift={
          selectedMarketGift ? platformGiftToUserGift(selectedMarketGift) : null
        }
        buyMode={{
          onBuy: confirmBuy,
          isBuying: purchaseMutation.isPending,
        }}
      />

      <DepositSheet
        isOpen={isDepositOpen}
        setIsOpen={setIsDepositOpen}
        initialAmount={depositInitialAmount}
        depositUiSource={
          depositInitialAmount != null && depositInitialAmount > 0
            ? "inventory_shortfall"
            : "inventory"
        }
      />
    </div>
  )
}

export default Inventory
